import { FC, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import FormFieldsContainer from "../../components/ui/FormFieldsContainer";
import { Button, Card, Form, FormField, Input } from "@rothoblaas/design-system";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../context/notificationContext";
import useI18n from "../../hooks/useTranslations";
import { FullProject, ProjectLight, UpsertProjectBody } from "../../http/types/projects";
import { UserLight } from "../../http/types/users";
import { useProjectsApi } from "../../http/useProjects";

interface ProjectFormBody {
  name: string | null;
  location: string | null;
  designer: string | null;
  customer: string | null;
}

interface NewEditProjectProps {
  project?: FullProject;
  disabled?: boolean;
  isLoading?: boolean;
  disableNavigationOnSuccess?: boolean;
  onSuccess?: (project?: ProjectLight) => void;
  onClose?: () => void;
}

export const ProjectForm: FC<NewEditProjectProps> = ({
  project,
  disabled,
  isLoading,
  disableNavigationOnSuccess = false,
  onSuccess,
  onClose,
}: NewEditProjectProps) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const navigate = useNavigate();
  const projectsApi = useProjectsApi();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const defaultValues: ProjectFormBody = {
    name: project?.name ?? null,
    location: project?.location ?? null,
    designer: project?.designer ?? null,
    customer: project?.customer ?? null,
  };

  const onSubmit = () => (formData: ProjectFormBody) => {
    setIsSubmitting(true);

    const body: UpsertProjectBody = {
      name: formData.name ?? "",
      location: formData.location ?? "",
      designer: formData.designer ?? "",
      customer: formData.customer ?? "",
    };

    project?.id
      ? projectsApi
          .updateMyProject(project.id, body)
          .then(() => {
            dispatch(showSuccessNotification(i18n.translation));
            onSuccess?.();
          })
          .catch((err) => dispatch(showErrorNotification(i18n, err)))
          .finally(() => setIsSubmitting(false))
      : projectsApi
          .insertMyProject(body)
          .then((res) => {
            if (!disableNavigationOnSuccess) {
              navigate(`/my-projects/${res.id}`, { replace: true });
            }
            onSuccess?.(res);
          })
          .then(() => dispatch(showSuccessNotification(i18n.translation)))
          .catch((err) => dispatch(showErrorNotification(i18n, err)))
          .finally(() => setIsSubmitting(false));
  };

  return (
      <Form<ProjectFormBody>
        submitText={i18n.translation.common.save}
        disabled={disabled || isSubmitting}
        defaultValues={defaultValues}
        secondaryButton={
          onClose ? (
            <Button onClick={onClose} variant="secondary">
              {i18n.translation.common.cancel}
            </Button>
          ) : undefined
        }
        onSubmit={onSubmit}
      >
        {({ register, formState: { errors }, reset }) => {
          useEffect(() => {
            reset(defaultValues);
          }, [project]);

        return (
          <FormFieldsContainer>
            {/* NAME */}
            <FormField label={i18n.translation.common.name} error={errors.name}>
              {({ labelId, isOptional, isInvalid }) => (
                <Input
                  id={labelId}
                  isInvalid={isInvalid}
                  disabled={disabled || isLoading}
                  {...register("name", {
                    required: !isOptional,
                    minLength: 1,
                  })}
                />
              )}
            </FormField>

            {/* LOCATION */}
            <FormField label={i18n.translation.common.location} error={errors.location}>
              {({ labelId, isOptional, isInvalid }) => (
                <Input
                  id={labelId}
                  isInvalid={isInvalid}
                  disabled={disabled || isLoading}
                  {...register("location", {
                    required: !isOptional,
                    minLength: 1,
                  })}
                />
              )}
            </FormField>

            {/* DESIGNER */}
            <FormField label={i18n.translation.common.designer} error={errors.designer}>
              {({ labelId, isOptional, isInvalid }) => (
                <Input
                  id={labelId}
                  isInvalid={isInvalid}
                  disabled={disabled || isLoading}
                  {...register("designer", {
                    required: !isOptional,
                    minLength: 1,
                  })}
                />
              )}
            </FormField>

            {/* CUSTOMER */}
            <FormField label={i18n.translation.common.customer} error={errors.customer}>
              {({ labelId, isOptional, isInvalid }) => (
                <Input
                  id={labelId}
                  isInvalid={isInvalid}
                  disabled={disabled || isLoading}
                  {...register("customer", {
                    required: !isOptional,
                    minLength: 1,
                  })}
                />
              )}
            </FormField>
          </FormFieldsContainer>
        );
      }}
    </Form>
  );
};

interface AdminProjectFormBody {
  name: string | null;
  location: string | null;
  designer: string | null;
  customer: string | null;
  owner: UserLight | null;
  editor: UserLight | null;
}

interface NewEditAdminProjectProps {
  project?: FullProject;
  disabled?: boolean;
  isLoading?: boolean;
  disableNavigationOnSuccess?: boolean;
  onSuccess?: (project?: ProjectLight) => void;
  onClose?: () => void;
}

export const AdminProjectForm: FC<NewEditAdminProjectProps> = ({
  project,
  disabled,
  isLoading,
  disableNavigationOnSuccess = false,
  onSuccess,
  onClose,
}: NewEditAdminProjectProps) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const navigate = useNavigate();
  const projectsApi = useProjectsApi();
  const newProject = !project;

  const [isSubmitting, setIsSubmitting] = useState(false);

  const defaultValues: AdminProjectFormBody = {
    name: project?.name ?? null,
    location: project?.location ?? null,
    designer: project?.designer ?? null,
    customer: project?.customer ?? null,
    owner: project?.owner ?? null,
    editor: project?.editor ?? null,
  };

  const onSubmit = () => (formData: AdminProjectFormBody) => {
    setIsSubmitting(true);

    const body: UpsertProjectBody = {
      name: formData.name ?? "",
      location: formData.location ?? "",
      designer: formData.designer ?? "",
      customer: formData.customer ?? "",
      // owner: formData.owner?.email ? ({ email: formData.owner.email } as UserLight) : ({} as UserLight), // todo is it possible a change of owner?
      // editor: formData.editor?.email ? ({ email: formData.editor.email } as UserLight) : ({} as UserLight),
    };

    project?.id
      ? projectsApi
          .updateAdminProject(project.id, body)
          .then(() => dispatch(showSuccessNotification(i18n.translation)))
          .catch((err) => dispatch(showErrorNotification(i18n, err)))
          .finally(() => setIsSubmitting(false))
      : projectsApi
          .insertAdminProject(body)
          .then((res) => {
            if (!disableNavigationOnSuccess) {
              navigate(`/admin/projects/${res.id}`, { replace: true });
            }
            onSuccess?.(res);
          })
          .then(() => dispatch(showSuccessNotification(i18n.translation)))
          .catch((err) => dispatch(showErrorNotification(i18n, err)))
          .finally(() => setIsSubmitting(false));
  };

  return (
    <Card shadow>
      <Form<AdminProjectFormBody>
        submitText={i18n.translation.common.save}
        disabled={disabled || isSubmitting}
        defaultValues={defaultValues}
        secondaryButton={
          onClose ? (
            <Button onClick={onClose} variant="secondary">
              {i18n.translation.common.cancel}
            </Button>
          ) : undefined
        }
        onSubmit={onSubmit}
      >
        {({ register, formState: { errors }, reset }) => {
          useEffect(() => {
            reset(defaultValues);
          }, [project]);

          return (
            <FormFieldsContainer>
              {/* NAME */}
              <FormField label={i18n.translation.common.name} error={errors.name}>
                {({ labelId, isOptional, isInvalid }) => (
                  <Input
                    id={labelId}
                    isInvalid={isInvalid}
                    disabled={disabled || isLoading}
                    {...register("name", {
                      required: !isOptional,
                      minLength: 1,
                    })}
                  />
                )}
              </FormField>

              {/* LOCATION */}
              <FormField label={i18n.translation.common.location} error={errors.location}>
                {({ labelId, isOptional, isInvalid }) => (
                  <Input
                    id={labelId}
                    isInvalid={isInvalid}
                    disabled={disabled || isLoading}
                    {...register("location", {
                      required: !isOptional,
                      minLength: 1,
                    })}
                  />
                )}
              </FormField>

              {/* DESIGNER */}
              <FormField label={i18n.translation.common.designer} error={errors.designer}>
                {({ labelId, isOptional, isInvalid }) => (
                  <Input
                    id={labelId}
                    isInvalid={isInvalid}
                    disabled={disabled || isLoading}
                    {...register("designer", {
                      required: !isOptional,
                      minLength: 1,
                    })}
                  />
                )}
              </FormField>

              {/* CUSTOMER */}
              <FormField label={i18n.translation.common.customer} error={errors.customer}>
                {({ labelId, isOptional, isInvalid }) => (
                  <Input
                    id={labelId}
                    isInvalid={isInvalid}
                    disabled={disabled || isLoading}
                    {...register("customer", {
                      required: !isOptional,
                      minLength: 1,
                    })}
                  />
                )}
              </FormField>

              {/* OWNER */}
              <FormField label={i18n.translation.common.owner} error={errors.customer} hidden={newProject}>
                {({ labelId, isInvalid }) => (
                  <Input
                    id={labelId}
                    isInvalid={isInvalid}
                    disabled
                    {...register("owner.email", {
                      required: !newProject,
                      // minLength: 1,
                    })}
                  />
                )}
              </FormField>

            {/* EDITOR */}
            <FormField label={i18n.translation.common.editor} error={errors.customer} hidden={newProject}>
              {({ labelId, isInvalid }) => (
                <Input
                  id={labelId}
                  isInvalid={isInvalid}
                  disabled={true}
                  {...register("editor.email", {
                    required: !newProject,
                    // minLength: 1,
                  })}
                />
              )}
            </FormField>
          </FormFieldsContainer>
        );
      }}
    </Form>
    </Card>
    // </FlexContent>
  );
};
